/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Col, Form, Grid, Input, Modal, Row, Space, Table, Tooltip } from 'antd';
import { MenuOutlined, SearchOutlined } from '@ant-design/icons';
import ContactChatSessionsForm from '../Forms/ContactChatSessionsForm';
import Highlighter from 'react-highlight-words';
import BotAvatar from '../UI/BotAvatar';
import CustomRangePicker from '../Forms/Fields/CustomRangePicker';

// Misc imports
import is from '../../utils/is';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useFetchOrganizationSubscribersQuery } from '../../store/organizationSubscribers/organizationSubscribersApi';
import { DEFAULT_DATETIME_FORMAT, getPresetDateRanges, DATETIME_BACKEND_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';

// Constant declarations
const { useBreakpoint } = Grid;

const OrganizationSubscribersTable = () => {
  const [form] = Form.useForm();
  const { orgId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const [searchParams] = useSearchParams();

  const { data: organization, isFetching: isFetchingOrganization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  let filterTimeout = 0;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilter, setSearchFilter] = useState({ search: '' });
  const [selectedItem, setSelectedItem] = useState(null);

  const [isChatSessionsModalOpen, setIsChatSessionsModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: organizationSubscribers, isFetching: isFetchingOrganizationSubscribers } = useFetchOrganizationSubscribersQuery({ orgId, urlParams: {
    page: currentPage - 1,
    pageSize: pageSize,
    ...(searchParams.get('searchTerm') && { search: searchParams.get('searchTerm') }),
    startDate: `${searchParams.get('startDate')}${encodeURIComponent(moment().tz(orgTimezone)?.format('Z'))}`,
    endDate: `${searchParams.get('endDate')}${encodeURIComponent(moment().tz(orgTimezone)?.format('Z'))}`,
    sortBy: searchParams.get('sortBy') || 'active',
    order: searchParams.get('order') || 'desc'
  } }, { skip: !organization || !searchParams.get('startDate') || !searchParams.get('endDate') });

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'activeAt';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  const pickerDates = searchParams.get('startDate') && searchParams.get('endDate') ? [
    moment.tz(decodeURIComponent(searchParams.get('startDate')), orgTimezone),
    moment.tz(decodeURIComponent(searchParams.get('endDate')), orgTimezone)
  ] : [moment.tz(moment(), orgTimezone).startOf('day'), moment.tz(moment(), orgTimezone).endOf('day')];

  useEffect(() => {
    if (organization) {
      form.setFieldsValue({ rangePicker: pickerDates });
      updateUrlSearchParams(Number(searchParams.get('page')) + 1, { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'desc' : 'asc' });
    }
  }, [organization, orgTimezone]);

  const openChatSessionsModal = (contact) => {
    setSelectedItem(contact);
    setIsChatSessionsModalOpen(true);
  };

  const closeChatSessionsModal = () => {
    setIsChatSessionsModalOpen(false);
  };

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'fullName' && sortingInfo.order,
      ellipsis: true,
      width: isMobileOrTabletSize ? '30%' : '',
      render: (_text, record) => (
        <Col >
          {isMobileOrTabletSize
            ? mobileViewNameColumn(record)
            : <Highlighter
              className={'bg-none'}
              highlightClassName="bg-highlight p-0"
              searchWords={[searchFilter.search]}
              autoEscape
              textToHighlight={record.fullName}
            />
          }
        </Col>
      )
    },
    {
      title: t('Email'),
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'email' && sortingInfo.order,
      responsive: ['xl'],
      render: (_text, record) => (
        <Highlighter
          highlightClassName="bg-highlight p-0"
          searchWords={[searchFilter.search]}
          autoEscape
          textToHighlight={record.email}
        />
      )
    },
    {
      key: 'tenant',
      title: t('Agent'),
      width: isMobileOrTabletSize ? '14%' : '25%',
      render: (subscriber) => {
        return (
          <BotAvatar
            bot={{
              displayName: subscriber.tenant.displayName,
              image: subscriber.tenant.image
            }}
            avatarSize={24}
          />
        );
      }
    },
    {
      title: t('First Interaction'),
      dataIndex: 'activeAt',
      key: 'activeAt',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'activeAt' && sortingInfo.order,
      render: activeAt => {
        return (
          moment(activeAt).format(DEFAULT_DATETIME_FORMAT)
        );
      }
    },
    {
      title: t('Last Interaction'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'updatedAt' && sortingInfo.order,
      render: updateAt => {
        return (
          moment(updateAt).format(DEFAULT_DATETIME_FORMAT)
        );
      }
    },
    {
      key: 'actions',
      title: t('Action'),
      align: 'center',
      width: 120,
      responsive: ['xl'],
      render: (_, record) => (
        <Space size={2} wrap={false} align="center">
          <Tooltip overlayClassName="pointer-events-none" title={t('Details')}>
            <Button
              type="text"
              shape="circle"
              icon={<MenuOutlined
                onClick={() => openChatSessionsModal(record)}
              />}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const mobileViewNameColumn = (record) => {
    return (
      <Fragment>
        <Highlighter
          className={'fs-sm bg-none p-0 fw-bold white-space-pre-line'}
          highlightClassName="bg-highlight p-0"
          searchWords={[searchFilter.search]}
          autoEscape
          textToHighlight={record.fullName + '\n'}
        />
        <Tooltip
          overlayClassName="pointer-events-none"
          className="ml-1"
          placement="topLeft"
          title={record.email}
        >
          <Highlighter
            className={'fs-sm bg-none mr-20 p-0'}
            highlightClassName="bg-highlight p-0"
            searchWords={[searchFilter.search]}
            autoEscape
            textToHighlight={record.email}
          />
        </Tooltip>
      </Fragment>
    );
  };

  const updateUrlSearchParams = (current = 1, pageSize = DEFAULT_PAGINATION_SIZE, sorting = { activeAt: 'desc' }) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);

    urlSearchParams.set('page', current - 1);
    urlSearchParams.set('pageSize', pageSize);
    const rangePicker = form.getFieldValue('rangePicker');
    let startDate = rangePicker[0];
    let endDate = rangePicker[1];
    const searchTerm = form.getFieldValue('search');

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    if (startDate) {
      const plainDateString = startDate.format(DATETIME_BACKEND_FORMAT);
      urlSearchParams.set('startDate', encodeURIComponent(`${plainDateString}`));
    } else {
      urlSearchParams.delete('startDate');
    }

    if (endDate) {
      const plainDateString = endDate.format(DATETIME_BACKEND_FORMAT);
      urlSearchParams.set('endDate', encodeURIComponent(`${plainDateString}`));
    } else {
      urlSearchParams.delete('endDate');
    }

    if (searchTerm) {
      urlSearchParams.set('searchTerm', encodeURIComponent(searchTerm));
    } else {
      urlSearchParams.set('searchTerm', encodeURIComponent(''));
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const handleTableChange = (pagination, _filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'activeAt' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting);
  };

  return (
    <Fragment>
      <Row gutter={24} wrap={screens.xs && !screens.sm}>
        <Col flex="auto" className="mb-2">
          <Form
            layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
            name="contactChatSessionDates"
            initialValues={{
              rangePicker: pickerDates,
              search: searchParams.get('searchTerm') ? decodeURIComponent(searchParams.get('searchTerm')) : ''
            }}
            form={form}
          >
            <Form.Item
              name="search"
              style={{ maxWidth: screens.xs ? '100%' : 280 }}
              className="w-100 mb-2"
            >
              <Input
                size="large"
                placeholder={t('Search for Name or Email')}
                allowClear
                prefix={<SearchOutlined className="text-muted" />}
                onChange={(e) => {
                  clearTimeout(filterTimeout);
                  filterTimeout = setTimeout(() => {
                    setCurrentPage(1);
                    setSearchFilter({ ...searchFilter, search: e.target.value });
                    updateUrlSearchParams();
                  }, 700);
                }}
              />
            </Form.Item>

            <Form.Item
              name="rangePicker"
              className="mb-2"
              rules={[{
                required: false,
                message: t('Please select a start date.')
              }]}
            >
              <CustomRangePicker
                disabled={isFetchingOrganizationSubscribers || isFetchingOrganization}
                size="large"
                disabledDate={(current) => {
                  return current && current > moment.tz(moment(), orgTimezone).endOf('day');
                }}
                allowClear={false}
                onChange={(dates) => {
                  if (dates === null) {
                    form.setFieldsValue({ rangePicker: [
                      moment.tz(moment(), orgTimezone).startOf('day'),
                      moment.tz(moment(), orgTimezone).endOf('day')
                    ] });
                  } else {
                    form.setFieldsValue({ rangePicker: [
                      dates[0],
                      dates[1]
                    ] });
                  }
                  updateUrlSearchParams();
                }}
                ranges={getPresetDateRanges(orgTimezone)}
                timezone={orgTimezone}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            rowKey="id"
            size="small"
            columns={columns}
            onChange={handleTableChange}
            pagination={{
              size: 'default',
              total: organizationSubscribers?.total,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Subscribers')}: ${total}`
            }}
            dataSource={organizationSubscribers && is.array(organizationSubscribers.items) && organizationSubscribers.items}
            loading={isFetchingOrganizationSubscribers}
          />
        </Col>
      </Row>

      <Modal
        title={t('Contact\'s Chat Sessions')}
        open={isChatSessionsModalOpen}
        onOk={closeChatSessionsModal}
        onCancel={closeChatSessionsModal}
        destroyOnClose={true}
        footer={null}
        style={{ maxWidth: 1650 }}
        width={'95%'}
        centered
      >
        <ContactChatSessionsForm contact={selectedItem} />
      </Modal>
    </Fragment>
  );

};

export default OrganizationSubscribersTable;
